import type { OBConsultMeasurements } from '../../types/pregnancy';

export interface PregnancyDetailState {
  activeTab: 'gravidogram' | 'consultations' | 'outcome';
  showNewConsult: boolean;
  showDeliveryVaginal: boolean;
  showDeliveryCaesarean: boolean;
  showLossMiscarriage: boolean;
  showLossTermination: boolean;
  obMeasurements: OBConsultMeasurements;
  selectedPregnancyId: string;
}

export const emptyMeasurements: OBConsultMeasurements = {
  gestationWeeks: null,
  gestationDays: null,
  sfh: null,
  presentation: '',
  headAboveBrim: null,
  fetalHeartRate: null,
  fetalMovements: null,
  bpSystolic: null,
  bpDiastolic: null,
  urine: '',
  haemoglobin: null,
  supplements: { iron: false, folate: false, calcium: false },
  tbScreenDone: false,
  nextVisitDate: null,
};

export const initialState: PregnancyDetailState = {
  activeTab: 'gravidogram',
  showNewConsult: false,
  showDeliveryVaginal: false,
  showDeliveryCaesarean: false,
  showLossMiscarriage: false,
  showLossTermination: false,
  obMeasurements: { ...emptyMeasurements },
  selectedPregnancyId: '',
};

export type PregnancyDetailAction =
  | { type: 'SET_TAB'; tab: PregnancyDetailState['activeTab'] }
  | { type: 'OPEN_NEW_CONSULT' }
  | { type: 'CLOSE_NEW_CONSULT' }
  | { type: 'OPEN_DELIVERY_VAGINAL' }
  | { type: 'CLOSE_DELIVERY_VAGINAL' }
  | { type: 'OPEN_DELIVERY_CAESAREAN' }
  | { type: 'CLOSE_DELIVERY_CAESAREAN' }
  | { type: 'OPEN_LOSS_MISCARRIAGE' }
  | { type: 'CLOSE_LOSS_MISCARRIAGE' }
  | { type: 'OPEN_LOSS_TERMINATION' }
  | { type: 'CLOSE_LOSS_TERMINATION' }
  | { type: 'SET_OB_MEASUREMENTS'; measurements: OBConsultMeasurements }
  | { type: 'RESET_OB_MEASUREMENTS' }
  | { type: 'SET_SELECTED_PREGNANCY'; id: string };

export function pregnancyDetailReducer(
  state: PregnancyDetailState,
  action: PregnancyDetailAction,
): PregnancyDetailState {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, activeTab: action.tab };
    case 'OPEN_NEW_CONSULT':
      return { ...state, showNewConsult: true };
    case 'CLOSE_NEW_CONSULT':
      return { ...state, showNewConsult: false, obMeasurements: { ...emptyMeasurements } };
    case 'OPEN_DELIVERY_VAGINAL':
      return { ...state, showDeliveryVaginal: true };
    case 'CLOSE_DELIVERY_VAGINAL':
      return { ...state, showDeliveryVaginal: false };
    case 'OPEN_DELIVERY_CAESAREAN':
      return { ...state, showDeliveryCaesarean: true };
    case 'CLOSE_DELIVERY_CAESAREAN':
      return { ...state, showDeliveryCaesarean: false };
    case 'OPEN_LOSS_MISCARRIAGE':
      return { ...state, showLossMiscarriage: true };
    case 'CLOSE_LOSS_MISCARRIAGE':
      return { ...state, showLossMiscarriage: false };
    case 'OPEN_LOSS_TERMINATION':
      return { ...state, showLossTermination: true };
    case 'CLOSE_LOSS_TERMINATION':
      return { ...state, showLossTermination: false };
    case 'SET_OB_MEASUREMENTS':
      return { ...state, obMeasurements: action.measurements };
    case 'RESET_OB_MEASUREMENTS':
      return { ...state, obMeasurements: { ...emptyMeasurements } };
    case 'SET_SELECTED_PREGNANCY':
      return { ...state, selectedPregnancyId: action.id };
    default:
      return state;
  }
}
